import { useState, type ReactNode } from 'react';
import { ArrowLeft, ExternalLink, GitBranch } from 'lucide-react';
import { GIT_PROVIDER_LABELS, parsePrUrl } from '@reflex/shared';
import { Badge } from '../ui/badge';
import { CopyButton } from './CopyButton';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { RepoLabel } from './RepoLabel';
import { PR_DISPLAY_META, type PrDisplayState } from './pr-state-presentation';
import { cn } from '../../lib/utils';

/**
 * The PR badge's width-affecting classes, exported so a host that measures
 * chips before laying them out (the agent header's association strip) can
 * reproduce this chip's rendered width without duplicating its text in the
 * DOM. Carries the default (`open`) colors; the chip overrides only the color
 * utilities per state, which never change its width.
 */
export const PULL_REQUEST_CHIP_BADGE_CLASSNAME =
  'cursor-pointer gap-0.5 border-status-info/25 bg-status-info/10 px-1.5 py-0 text-[10px] font-medium text-status-info hover:bg-status-info/20';

/** The live facts about a PR the hover card fills itself in with. */
export interface PullRequestChipDetails {
  title?: string | null;
  baseBranch?: string | null;
  headBranch?: string | null;
}

export interface PullRequestChipProps {
  /** The pull request's GitHub (or GitLab MR) URL. Provider, repo, and number are parsed from it. */
  url: string;
  /**
   * The state to render. Callers resolve this themselves (from a stored
   * association, from GitHub, or both) so this component stays fetch-free and
   * usable from any package. Defaults to `open`.
   */
  state?: PrDisplayState;
  /** Live PR facts for the hover card; `null` until the caller has them. */
  details?: PullRequestChipDetails | null;
  /** True while `details` is being fetched — shows a hint inside the card. */
  loading?: boolean;
  /**
   * Fires when the hover card opens or closes, so a caller can fetch the
   * details lazily rather than on mount.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Extra line in the card's footer — how the surface relates to the PR (e.g.
   * "Opened by this agent"). Omitted for a bare PR reference.
   */
  footnote?: ReactNode;
  /**
   * Collapse to the state icon and border, dropping the `#<number>` label,
   * when the host strip is too narrow to show it. The number stays in the aria
   * label and hover card, so the reference is still reachable.
   */
  iconOnly?: boolean;
  className?: string;
  /**
   * Prefix for this chip's test ids (`<prefix>`, `<prefix>-popover`,
   * `-title`, `-repo`, `-state`, `-branches`, `-copy-branch`, `-footnote`).
   * Lets each host keep the ids its own tests already assert on.
   */
  testIdPrefix?: string;
}

/** States whose name is worth folding into the accessible label. */
const LABELLED_STATES: ReadonlySet<PrDisplayState> = new Set(['merged', 'closed', 'draft']);

/**
 * Compact, inline pull-request reference: a state-colored pull-request icon
 * plus `#<number>` (parsed from `url`) that links straight to the PR. Hovering
 * opens a GitHub-style card with the PR title, its state, the `base <- head`
 * branch flow (with a button that copies the head branch), and an optional
 * footnote.
 *
 * Pure and fetch-free — the caller resolves `state` and `details` and passes
 * them down. That is what lets the same chip serve the agent header (where a
 * stored association seeds the state) and the chat transcript (where a PR URL
 * an agent printed is all we have), instead of each growing its own popover.
 */
export function PullRequestChip({
  url,
  state = 'open',
  details,
  loading,
  onOpenChange,
  footnote,
  iconOnly,
  className,
  testIdPrefix = 'pull-request-chip',
}: PullRequestChipProps) {
  const parsed = parsePrUrl(url);
  const repoSlug = parsed ? parsed.repoSlug : null;
  const [open, setOpen] = useState(false);

  const providerLabel = GIT_PROVIDER_LABELS[parsed?.provider ?? 'github'];
  const requestLabel = parsed?.requestLabel ?? 'pull request';
  const requestTitle = requestLabel === 'merge request' ? 'Merge request' : 'Pull request';
  const referencePrefix = parsed?.referencePrefix ?? '#';
  const meta = PR_DISPLAY_META[state];
  const StateIcon = meta.icon;
  const label = parsed ? `${referencePrefix}${parsed.number}` : 'PR';
  const stateSuffix = LABELLED_STATES.has(state) ? ` (${meta.label.toLowerCase()})` : '';
  const ariaLabel = parsed
    ? `Open ${requestLabel} ${referencePrefix}${parsed.number} on ${repoSlug}${stateSuffix}`
    : `Open pull request${stateSuffix}`;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  return (
    <HoverCard open={open} onOpenChange={handleOpenChange} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          data-testid={testIdPrefix}
          data-pr-state={state}
          className="inline-flex items-center rounded-md align-middle transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge
            variant="outline"
            className={cn(PULL_REQUEST_CHIP_BADGE_CLASSNAME, meta.badgeColor, className)}
          >
            <StateIcon className="h-2.5 w-2.5" aria-hidden="true" />
            {iconOnly ? null : <span>{label}</span>}
          </Badge>
        </a>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-80 overflow-hidden p-0"
        data-testid={`${testIdPrefix}-popover`}
      >
        {/* Header: state icon + linked title, then the repo/number line. */}
        <div className="flex items-start gap-2.5 px-4 pt-4">
          <StateIcon className={cn('mt-0.5 h-4 w-4 shrink-0', meta.iconColor)} aria-hidden="true" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm font-semibold leading-snug text-foreground line-clamp-2 hover:underline"
              data-testid={`${testIdPrefix}-title`}
            >
              {details?.title ?? requestTitle}
            </a>
            {parsed && repoSlug ? (
              <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                <RepoLabel
                  repo={repoSlug}
                  href={parsed.repoWebUrl}
                  provider={parsed.provider}
                  className="min-w-0"
                  data-testid={`${testIdPrefix}-repo`}
                />
                <span className="shrink-0 tabular-nums">
                  {referencePrefix}
                  {parsed.number}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* State pill + base <- head branch flow. */}
        <div className="mt-3 flex flex-col gap-2 px-4">
          <span
            className={cn(
              'inline-flex w-fit items-center gap-1 rounded-full border border-current/20 bg-current/10 px-2 py-0.5 text-xs font-medium',
              meta.iconColor,
            )}
            data-testid={`${testIdPrefix}-state`}
          >
            <StateIcon className="h-3 w-3" aria-hidden="true" />
            {meta.label}
          </span>
          {details?.headBranch && details.baseBranch ? (
            <div
              className="flex min-w-0 items-center gap-1.5 rounded-md bg-muted/60 px-2 py-1 text-xs"
              data-testid={`${testIdPrefix}-branches`}
            >
              <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              {/* The base is short and shared ("main"); the head is the long,
                  disposable one, so it gives up the width first. Capped so a
                  long base cannot squeeze the head out entirely. */}
              <span className="max-w-[40%] shrink-0 truncate font-mono text-foreground">
                {details.baseBranch}
              </span>
              <ArrowLeft className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate font-mono text-foreground">{details.headBranch}</span>
              {/* The head branch is what you check out locally, so it is the
                  half of the flow worth copying — and the button sits against
                  that name, not at the row's edge, so what it copies is
                  unambiguous. */}
              <CopyButton
                text={details.headBranch}
                testId={`${testIdPrefix}-copy-branch`}
                ariaLabel="Copy branch name"
                copiedAriaLabel="Branch name copied"
              />
            </div>
          ) : open && loading ? (
            <span className="text-xs text-muted-foreground">Loading details…</span>
          ) : null}
        </div>

        {/* Footer: the host's note + the canonical provider link. */}
        <div className="mt-3 flex items-center justify-between gap-2 border-t bg-muted/30 px-4 py-2.5">
          {footnote ? (
            <span
              className="min-w-0 truncate text-xs text-muted-foreground"
              data-testid={`${testIdPrefix}-footnote`}
            >
              {footnote}
            </span>
          ) : (
            <span />
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${requestLabel} on ${providerLabel}`}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-status-info hover:underline"
          >
            View on {providerLabel}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
